import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbPageTitle, { IBreadcrumb } from '../../../components/IbPageTitle';
import { defaultApi } from '../../../apis';
import {
  AgentModel,
  AgentSettingsInput,
  AgentUpdatingRequest,
  ApiKeyScopeRefType,
  DataInterfaceType,
  ProviderModel,
  TraceProviderCreationRequest,
  TraceProviderModel,
  TraceProviderNames,
  TraceProviderUpdatingRequest,
  TraceSettings,
} from '../../../../gamma-api';
import { getAgentPath, getAgentsPath } from '../../../utils/pathUtils';
import { alertsSelectorAdd } from '../../../recoil';
import IbTabs from '../../../components/common/IbTabs';
import IbTabPane from '../../../components/common/IbTabPane';
import { LARGE_PAGE_SIZE } from '../../../constants';
import Settings from '../../../components/Settings';
import ApiKeys from '../../../components/ApiKeys';
import { getTranslateFunc } from '../../../utils/stringUtil';
import AvatarPicker from '../../../components/common/AvatarPicker';
import { ValidationErrors } from '../../../types';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import IbIcon from '../../../components/common/IbIcon';
import ExportButton from '../../../components/ExportButton';
import ImportUpload from '../../../components/ImportUpload';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbButton from '../../../components/common/IbButton';
import AgentModal from '../AgentsPage/AgentModal';
import ConfirmModal from '../AgentsPage/ConfirmModal';

import { CONTENT_CLASS_NAME, MAIN_CLASS_NAME, TITLE_CLASS_NAME } from './const';
import ToolsetsTab from './ToolsetsTab';
import TestingChatTab from './TestingChatTab';
import TracingTab from './TracingTab';
import RelationsTab from './RelationsTab';
import ThreadsTab from './ThreadsTab';
import RunsTab from './RunsTab';
import GuardrailsTab from './GuardrailsTab';
import MaskingTab from './MaskingTab';

enum AgentTabKey {
  Settings = 'settings',
  Toolsets = 'toolsets',
  Agents = 'agents',
  Chat = 'chat',
  Keys = 'keys',
  Tracing = 'tracing',
  Guardrails = 'guardrails',
  Masking = 'masking',
  Widget = 'widget',
  Threads = 'threads',
  Runs = 'runs',
}

interface IAgentPageProps {
  interfaceType: DataInterfaceType;
}

const AgentPage: React.FC<IAgentPageProps> = ({ interfaceType }) => {
  const { t } = useTranslation();
  const translatePrefix = interfaceType === DataInterfaceType.Api ? 'API' : 'Chat';
  const translate = getTranslateFunc(`Agent: ${translatePrefix}:`);

  const { push } = useHistory();

  const { folderId, agentId } = useParams<{ folderId: string; agentId: string }>();
  const tabKey = useParams<{ tabKey?: string }>().tabKey?.toLowerCase() ?? AgentTabKey.Settings;

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [agent, setAgent] = useState<AgentModel>();
  const [tabsStateKey, setTabsStateKey] = useState('default');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<ProviderModel[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [agentTraceProvider, setAgentTraceProvider] = useState<TraceProviderModel>();

  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [agentModalVisible, setAgentModalVisible] = useState(false);

  const [updateAgent, setUpdateAgent] = useState<AgentModel>();
  const [deleteAgent, setDeleteAgent] = useState<AgentModel>();

  const breadcrumbs: IBreadcrumb[] = [{ title: translate('Agents'), path: getAgentsPath(interfaceType, folderId) }];

  const setTabKey = (value: string) => {
    push(getAgentPath(interfaceType, folderId, agentId, value));
  };

  const goToAgents = () => {
    push(getAgentsPath(interfaceType, folderId));
  };

  const tryFetchAgentTraceProvider = async (agent: AgentModel) => {
    const traceProviderId = agent.trace.trace_provider_id;
    if (!traceProviderId) return;

    try {
      const response = await defaultApi.getOneTraceProviderGeneralTraceProvidersTraceProviderIdGet(traceProviderId);
      setAgentTraceProvider(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  };

  const loadAgent = async () => {
    if (!agentId) return;

    try {
      setLoading(true);
      setProvidersLoading(true);

      const agentResponse = await defaultApi.getOneAgent(agentId);
      const agent = agentResponse.data;
      setAgent(agent);

      const providersResponse = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviders(providersResponse.data.items || []);

      await tryFetchAgentTraceProvider(agent);
    } catch (err) {
      addAlert({ content: translate('Failed to load agent'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
      setProvidersLoading(false);
    }
  };
  useEffect(() => {
    loadAgent().finally();
  }, [agentId]);

  const saveTracingSettings = async (
    traceSettings?: TraceSettings,
    traceProvider?: TraceProviderModel,
    settings?: AgentSettingsInput
  ) => {
    if (!agent) {
      return;
    }
    if (!traceSettings && !traceProvider && !settings) {
      return;
    }

    try {
      let traceProviderId: string;
      if (agentTraceProvider) {
        await defaultApi.updateTraceProviderGeneralTraceProvidersTraceProviderIdPut(
          agentTraceProvider.trace_provider_id,
          {
            enabled: traceProvider?.enabled ?? false,
            name: traceProvider?.name ?? TraceProviderNames.Phoenix,
            settings: traceProvider?.settings ?? { api_base: '' },
            title: traceProvider?.title,
          } as TraceProviderUpdatingRequest
        );
        traceProviderId = agentTraceProvider.trace_provider_id;
      } else {
        const traceProviderCreationResponse = await defaultApi.createTraceProviderGeneralTraceProvidersPost({
          enabled: traceProvider?.enabled ?? false,
          name: traceProvider?.name ?? TraceProviderNames.Phoenix,
          settings: traceProvider?.settings ?? { api_base: '' },
          title: `${agent.name} - ${traceProvider?.name} tracing`,
        } as TraceProviderCreationRequest);
        traceProviderId = traceProviderCreationResponse.data.trace_provider_id;
      }

      const trace = {
        trace_provider_id: traceProviderId,
        project_name: traceSettings?.project_name,
      } as TraceSettings;
      await defaultApi.updateAgent(agent.agent_id, {
        name: agent.name,
        state: agent.state,
        settings: settings ?? agent.settings,
        trace,
      });

      const newAgent = {
        ...agent,
        settings: settings ?? agent.settings,
        trace,
      } as AgentModel;
      await tryFetchAgentTraceProvider(newAgent);
      setAgent(newAgent);
      addAlert({ content: t('Agent: Tracing settings were saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Agent: Failed to update tracing settings'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const saveAgentSettings = async (settings: AgentSettingsInput, showSuccessAlert = true) => {
    if (!agent) {
      return;
    }

    try {
      await defaultApi.updateAgent(agent.agent_id, {
        name: agent.name,
        state: agent.state,
        settings,
        trace: agent.trace,
      });

      const newAgent = {
        ...agent,
        settings,
      } as AgentModel;
      setAgent(newAgent);
      if (showSuccessAlert) {
        addAlert({ content: translate('Agent settings were saved successfully'), type: 'success' });
      }
    } catch (err) {
      addAlert({ content: translate('Failed to apply settings to agent'), type: 'error' });
    }
  };

  const onTabChange = (key: string) => setTabKey(key as AgentTabKey);

  const onUpdateAgentClick = () => {
    setUpdateAgent(agent);
    setAgentModalVisible(true);
  };

  const onDeleteAgentClick = () => {
    setDeleteAgent(agent);
  };

  const onImportFileFinish = async (newAgent: AgentModel) => {
    setAgent(newAgent);
    setTabsStateKey(newAgent.modified_on);
  };

  const onAgentModalEdit = async (
    newAgent: AgentUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!updateAgent) return;

    try {
      await defaultApi.updateAgent(updateAgent.agent_id, newAgent);
      await loadAgent();
      setAgentModalVisible(false);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Agent updating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onAgentModalCancel = () => {
    setAgentModalVisible(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteAgent) return;

    setDeleteConfirmLoading(true);
    try {
      await defaultApi.deleteAgent(deleteAgent.agent_id);
      goToAgents();
    } catch (e) {
      addAlert({
        content: translate('Agent deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
    setDeleteConfirmLoading(false);
    setDeleteAgent(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteAgent(undefined);
  };

  const onAgentSettingsChange = async (settings: AgentSettingsInput) => {
    await saveAgentSettings(settings);
  };

  const onGuardrailsSettingsChange = async (settings: AgentSettingsInput) => {
    await saveAgentSettings(settings, false);
  };

  const onMaskingSettingsChange = async (settings: AgentSettingsInput) => {
    await saveAgentSettings(settings);
  };

  const onAgentAvatarChangeAsync = async (avatarName: string) => {
    if (!agent) return;

    const newAgentSettings = {
      ...agent.settings,
      widget: {
        ...agent.settings.widget,
        avatar: {
          ...agent.settings.widget.avatar,
          name: avatarName,
        },
      },
    };

    await saveAgentSettings(newAgentSettings);
  };

  const onTracingSave = async (
    traceSettings?: TraceSettings,
    traceProvider?: TraceProviderModel,
    settings?: AgentSettingsInput
  ) => {
    await saveTracingSettings(traceSettings, traceProvider, settings);
  };

  const renderTitle = () => {
    const contextMenuItems = [
      {
        icon: <IbIcon iconName="efferent-three" size={20} />,
        text: translate('Context: Export'),
        render: (child) => (
          <ExportButton.AgentExport agentId={agentId} interfaceType={interfaceType}>
            {child}
          </ExportButton.AgentExport>
        ),
      },
      {
        icon: <IbIcon iconName="afferent-three" size={20} />,
        text: translate('Context: Import'),
        render: (child) => (
          <ImportUpload.AgentImport
            agentId={agentId}
            folderId={folderId}
            interfaceType={interfaceType}
            onFinish={onImportFileFinish}
          >
            {child}
          </ImportUpload.AgentImport>
        ),
      },
      {
        icon: <IbIcon iconName="edit" size={20} />,
        onSelect: onUpdateAgentClick,
        text: translate('Context: Edit'),
      },
      {
        icon: <IbIcon iconName="delete" size={20} />,
        onSelect: onDeleteAgentClick,
        text: translate('Context: Delete'),
      },
    ] as IIbContextMenuItem[];

    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);

    return (
      <div style={{ display: 'flex' }}>
        <span>{agent?.name}</span>
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={24} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={TITLE_CLASS_NAME}>
        {interfaceType === DataInterfaceType.Chat && (
          <AvatarPicker avatarName={agent?.settings.widget.avatar.name} onChange={onAgentAvatarChangeAsync} />
        )}
        <IbPageTitle breadcrumbs={breadcrumbs} loading={loading} title={renderTitle()} />
      </div>
      <IbTabs key={tabsStateKey} activeKey={tabKey} className={CONTENT_CLASS_NAME} onChange={onTabChange}>
        <IbTabPane key={AgentTabKey.Settings} tab={t('Agent: Tab: Settings')} tabKey={AgentTabKey.Settings}>
          <Settings.AgentGeneral
            agentId={agent?.agent_id}
            agentSettings={agent?.settings}
            interfaceType={interfaceType}
            providers={providers}
            providersLoading={providersLoading}
            onSave={onAgentSettingsChange}
          />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Toolsets} tab={t('Agent: Tab: Tools')} tabKey={AgentTabKey.Toolsets}>
          <ToolsetsTab agentId={agentId} providers={providers} providersLoading={providersLoading} />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Agents} tab={t('Agent: Tab: Agents')} tabKey={AgentTabKey.Agents}>
          <RelationsTab managingAgentId={agentId} />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Chat} tab={t('Agent: Tab: Testing chat')} tabKey={AgentTabKey.Chat}>
          {tabKey === AgentTabKey.Chat && <TestingChatTab agent={agent} />}
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Keys} tab={t('Agent: Tab: Keys')} tabKey={AgentTabKey.Keys}>
          <ApiKeys
            showCreationButton
            scopeRef={{ id: agentId, type: ApiKeyScopeRefType.Agent, externalId: agent?.external_id }}
          />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Tracing} tab={t('Agent: Tab: Tracing')} tabKey={AgentTabKey.Tracing}>
          <TracingTab agent={agent} agentTraceProvider={agentTraceProvider} onSave={onTracingSave} />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Guardrails} tab={t('Agent: Tab: Guardrails')} tabKey={AgentTabKey.Guardrails}>
          <GuardrailsTab
            agentSettings={agent?.settings}
            providers={providers}
            providersLoading={providersLoading}
            onSave={onGuardrailsSettingsChange}
          />
        </IbTabPane>
        <IbTabPane key={AgentTabKey.Masking} tab={t('Agent: Tab: Masking')} tabKey={AgentTabKey.Masking}>
          <MaskingTab agentSettings={agent?.settings} onSave={onMaskingSettingsChange} />
        </IbTabPane>
        {agent?.settings.interface.type === DataInterfaceType.Chat && (
          <>
            <IbTabPane key={AgentTabKey.Widget} tab={t('Agent: Tab: Widget')} tabKey={AgentTabKey.Widget}>
              <Settings.AgentWidget agentSettings={agent?.settings} onSave={onAgentSettingsChange} />
            </IbTabPane>
            <IbTabPane key={AgentTabKey.Threads} tab={t('Agent: Tab: Threads')} tabKey={AgentTabKey.Threads}>
              {tabKey === AgentTabKey.Threads && <ThreadsTab agent={agent} />}
            </IbTabPane>
          </>
        )}
        {agent?.settings.interface.type === DataInterfaceType.Api && (
          <IbTabPane key={AgentTabKey.Runs} tab={t('Agent: Tab: Runs')} tabKey={AgentTabKey.Runs}>
            {tabKey === AgentTabKey.Runs && <RunsTab agent={agent} />}
          </IbTabPane>
        )}
      </IbTabs>
      <AgentModal
        editAgent={updateAgent}
        interfaceType={interfaceType}
        visible={agentModalVisible}
        onCancel={onAgentModalCancel}
        onEdit={onAgentModalEdit}
      />
      <ConfirmModal.DeleteAgent
        interfaceType={interfaceType}
        loading={deleteConfirmLoading}
        visible={!!deleteAgent}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default AgentPage;
